import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Trash2, Plus, Edit3, Save, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "usl_admin_token";
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY) || ""}` });

const empty = { title: "", excerpt: "", body: "", cover_image: "", tag: "", published: true };

export default function Articles() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/admin/articles`, { headers: authHeader() }); setItems(data); }
    catch { toast.error("Failed to load."); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startNew = () => { setEditing("new"); setForm(empty); };
  const startEdit = (a) => { setEditing(a.id); setForm({ title: a.title, excerpt: a.excerpt || "", body: a.body, cover_image: a.cover_image || "", tag: a.tag || "", published: a.published }); };
  const cancel = () => { setEditing(null); setForm(empty); };

  const save = async () => {
    if (!form.title.trim() || !form.body.trim()) { toast.error("Title and body are required"); return; }
    try {
      if (editing === "new") {
        await axios.post(`${API}/admin/articles`, form, { headers: authHeader() });
        toast.success("Article published");
      } else {
        await axios.patch(`${API}/admin/articles/${editing}`, form, { headers: authHeader() });
        toast.success("Article updated");
      }
      cancel(); load();
    } catch { toast.error("Save failed"); }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete "${a.title}"?`)) return;
    try { await axios.delete(`${API}/admin/articles/${a.id}`, { headers: authHeader() }); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  if (editing) {
    return (
      <div data-testid="article-editor" className="space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-3xl text-[#F4EFE6]">{editing === "new" ? "New Insight" : "Edit Insight"}</h3>
          <div className="flex gap-2">
            <button data-testid="article-cancel" onClick={cancel} className="usl-btn-outline text-xs"><X size={14} /> Cancel</button>
            <button data-testid="article-save" onClick={save} className="usl-btn text-xs"><Save size={14} /> {editing === "new" ? "Publish" : "Update"}</button>
          </div>
        </div>
        <div className="bg-[#11171A] border border-[#F4EFE6]/10 p-8 grid md:grid-cols-2 gap-6">
          <div className="md:col-span-2"><label className="overline block mb-2">Title *</label><input data-testid="article-title" className="underline-input" value={form.title} onChange={onChange("title")} /></div>
          <div><label className="overline block mb-2">Tag</label><input data-testid="article-tag" className="underline-input" value={form.tag} onChange={onChange("tag")} placeholder="Planning · Brownfield · Market" /></div>
          <div><label className="overline block mb-2">Cover image URL</label><input data-testid="article-cover" className="underline-input" value={form.cover_image} onChange={onChange("cover_image")} /></div>
          <div className="md:col-span-2"><label className="overline block mb-2">Short excerpt (1–2 sentences)</label><input data-testid="article-excerpt" className="underline-input" value={form.excerpt} onChange={onChange("excerpt")} /></div>
          <div className="md:col-span-2">
            <label className="overline block mb-2">Body (Markdown supported — use **bold**, ## headings, lists)</label>
            <textarea data-testid="article-body" rows={20} className="underline-input resize-y" value={form.body} onChange={onChange("body")} style={{ fontFamily: 'ui-monospace, monospace', fontSize: '13px', lineHeight: '1.6' }} />
          </div>
          <div className="md:col-span-2 flex items-center gap-3">
            <input data-testid="article-published" id="pub" type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} />
            <label htmlFor="pub" className="text-sm text-[#E8E1D2]">Published (visible on /insights)</label>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div className="overline">{items.length} article{items.length === 1 ? "" : "s"}</div>
        <button data-testid="article-new" onClick={startNew} className="usl-btn text-xs"><Plus size={14} /> New Insight</button>
      </div>
      <div className="border border-[#F4EFE6]/10 bg-[#11171A]">
        {items.length === 0 ? (
          <div data-testid="articles-empty" className="p-10 text-center text-[#9BA39B] text-sm">No articles yet. Click &quot;New Insight&quot; to publish your first.</div>
        ) : items.map(a => (
          <div key={a.id} data-testid={`article-row-${a.id}`} className="grid grid-cols-12 gap-3 px-5 py-4 items-center text-sm border-b border-[#F4EFE6]/10">
            <div className="col-span-6">
              <div className="font-semibold text-[#F4EFE6]">{a.title}</div>
              <div className="text-[11px] text-[#9BA39B] mt-1">/{a.slug}</div>
            </div>
            <div className="col-span-2 text-[#C9A24B] text-xs uppercase tracking-wider">{a.tag || "—"}</div>
            <div className="col-span-2">
              <span className={`text-[10px] tracking-[0.22em] uppercase font-semibold border px-2 py-1 ${a.published ? "border-emerald-400/40 text-emerald-300" : "border-[#9BA39B]/40 text-[#9BA39B]"}`}>{a.published ? "Published" : "Draft"}</span>
            </div>
            <div className="col-span-2 flex justify-end gap-2">
              <button data-testid={`article-edit-${a.id}`} onClick={() => startEdit(a)} className="text-[#9BA39B] hover:text-[#C9A24B] p-1.5"><Edit3 size={16} /></button>
              <button data-testid={`article-delete-${a.id}`} onClick={() => remove(a)} className="text-[#9BA39B] hover:text-rose-400 p-1.5"><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
